import React, { useEffect } from "react";
import { X, Loader2, BarChart3 } from "lucide-react";
import { AppImages } from "../../common/ImagePath";
import CustomButton from "../ui/CustomButton";
import CurrencyIcon from "../ui/CurrencyIcon";
import {
  DEMO_AMOUNT_LABEL,
  formatCurrencyAmount,
  isAmountHidden,
  resolveDisplayCurrency,
} from "../../common/currencies";

const RIBBON_BY_STATUS = {
  win: AppImages.winRibbon,
  loss: AppImages.lossRibbon,
  tie: AppImages.tieRibbon,
  void: AppImages.voidRibbon,
  reveal: AppImages.awaitingRibbon,
  waiting: AppImages.ongoingRibbon,
  pending: AppImages.ongoingRibbon,
};

const RIBBON_VIDEO_BY_STATUS = {
  win: AppImages.winRibbonVideo,
  loss: AppImages.gameResultRibbonVideo,
  tie: AppImages.tieRibbonVideo,
};

const YOU_RIBBON_GRADIENT_BY_STATUS = {
  win: "linear-gradient(180deg, #53D337 0%, #368C24 100%)",
  loss: "linear-gradient(180deg, #F84E4E 0%, #9B2B2B 100%)",
  tie: "linear-gradient(180deg, #4E89F8 0%, #3B67B9 100%)",
  void: "linear-gradient(180deg, #4E89F8 0%, #3B67B9 100%)",
  reveal: "linear-gradient(180deg, #9B6EF8 0%, #5E3BB9 100%)",
  waiting: "linear-gradient(180deg, #9B6EF8 0%, #5E3BB9 100%)",
  pending: "linear-gradient(180deg, #9B6EF8 0%, #5E3BB9 100%)",
};

const MatchResultSummaryPopUp = ({
  amount = 0,
  userName,
  userScore,
  opponentName,
  opponentScore,
  sunburstImage,
  onMatchStats,
  status,
  result,
  isOpen = true,
  onClose,
  isPending = false,
  currencyCode,
  onPlayAgain,
  isPlayAgainLoading = false,
  entryFee = 0,
  matchId,
  breakdown,
}) => {
  const displayCurrency = resolveDisplayCurrency(currencyCode);
  // Pink (gem) vs green (cash) theme keys off the data currency.
  const isGem = currencyCode === "GEMS";
  const amountValue = formatCurrencyAmount(amount, displayCurrency);
  const entryFeeValue = formatCurrencyAmount(entryFee, displayCurrency);
  const hideAmounts = isAmountHidden(currencyCode);
  const effectiveStatus = isPending && (status === "win" || status === "loss" || status === "tie")
    ? "pending"
    : status;
  const ribbonImage = RIBBON_BY_STATUS[effectiveStatus] ?? AppImages.ongoingRibbon;
  const ribbonVideo = RIBBON_VIDEO_BY_STATUS[effectiveStatus];
  const isTerminal = effectiveStatus === "win" || effectiveStatus === "loss" || effectiveStatus === "tie";
  const hideOpponentCard = effectiveStatus === "waiting" || effectiveStatus === "void";
  const youRibbonGradient =
    YOU_RIBBON_GRADIENT_BY_STATUS[effectiveStatus] ?? YOU_RIBBON_GRADIENT_BY_STATUS.loss;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200 px-4"
      onClick={
        onClose ? (e) => e.target === e.currentTarget && onClose() : undefined
      }
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm bg-[rgb(15,15,15)] rounded-2xl shadow-xl relative animate-in zoom-in-95 duration-200 overflow-hidden">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-black flex items-center justify-center text-white hover:text-white hover:bg-black/80 transition-colors border border-white/10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <div className="relative">
          {/* {sunburstImage && (
            <>
              <img
                src={sunburstImage}
                alt="background"
                className="absolute inset-0 w-full h-full object-cover opacity-10 mix-blend-screen"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#1E1D1D]/30 via-[#1E1D1D]/40 to-[#1E1D1D]" />
            </>
          )} */}
          {ribbonVideo ? (
            <video
              src={ribbonVideo}
              autoPlay
              muted
              playsInline
              loop
              aria-label={result || effectiveStatus}
              className="w-full block"
            />
          ) : (
            <img
              src={ribbonImage}
              alt={result || effectiveStatus}
              className="w-full block"
            />
          )}
          {effectiveStatus === "win" && !hideAmounts && (
            <div className="absolute inset-0 flex items-center justify-center translate-y-12 z-10 font-extrabold text-white font-saira">
              <span className="flex items-center justify-center gap-1 text-3xl">
                <CurrencyIcon code={displayCurrency} className="h-4 w-4" symbolClassName="text-[var(--brand)]" />
                {amountValue}
              </span>
            </div>
          )}
          {effectiveStatus === "loss" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center translate-y-8 z-10 text-white">
              <span className="text-sm text-center">Better luck next time!</span>
              {userScore === null && (
                <span className="text-sm text-center">Did not finish</span>
              )}
            </div>
          )}
          {effectiveStatus === "tie" && (
            <div className="absolute inset-0 flex items-center justify-center translate-y-8 z-10 text-white">
              <span className="text-sm text-center">Your entry has been refunded</span>
            </div>
          )}
          {!isTerminal && result && (
            <div className="absolute inset-0 flex items-center justify-center translate-y-8 z-10 text-white">
              <span className="text-sm text-center">{result}</span>
            </div>
          )}
        </div>

        {/* ROW 2 - Scores Section */}
        <div className="grid grid-cols-2 gap-4 mb-6 px-6">
          <div className="relative text-center rounded-xl p-4">
            {(status === "win" || status === "loss") && (
              <div
                className="absolute inset-0 rounded-xl"
                style={{
                  background: `${status === "win" ? "radial-gradient(circle at center, rgba(34,197,94,0.22) 0%, rgba(34,197,94,0.12) 30%, rgba(34,197,94,0.05) 50%, rgba(0,0,0,0) 75%)" : "radial-gradient(50% 50% at 50% 50%, #5B1718 0%, rgba(91, 23, 24, 0) 100%)"}`,
                }}
              />
            )}

            <div className="relative z-10 space-y-1">
              <div
                className={`inline-block font-bold text-xs px-4 py-1 text-center ${effectiveStatus === "win" ? "text-black" : "text-white"}`}
                style={{
                  borderRadius: "35.947px",
                  background: youRibbonGradient,
                }}
              >
                You
              </div>
              <div className="text-[16px]">{userName}</div>
              <div className="text-2xl font-bold">{userScore ?? "-"}</div>
            </div>
          </div>

          {/* Opponent Card */}
          <div className="relative text-center rounded-xl p-4">
            <div className="relative z-10 space-y-1">
              <div
                className="inline-block font-bold text-xs px-4 py-1 text-center text-black"
                style={{
                  borderRadius: "35.947px",
                  background: "linear-gradient(180deg, #B5B5B5 0%, #6E6E6E 100%)",
                }}
              >
                Opponent
              </div>
              <div className="text-[16px]">{opponentName ?? "-"}</div>
              <div className="text-2xl font-bold">
                {hideOpponentCard ? "-" : (opponentScore ?? "-")}
              </div>
            </div>
          </div>
        </div>

        {onMatchStats && breakdown?.length > 0 && (
          <div className="flex justify-center pb-5">
            <button
              type="button"
              onClick={onMatchStats}
              className="flex items-center gap-2 text-[12px] font-bold text-white px-5 py-1.5 rounded-full bg-gradient-to-b from-blackShade3 to-blackShade5 hover:opacity-90 transition"
            >
              <BarChart3 className="w-4 h-4" />
              Match Stats
            </button>
          </div>
        )}

        {onPlayAgain && (
          <div className="px-6 pb-6">
            <CustomButton
              onClick={onPlayAgain}
              disabled={isPlayAgainLoading}
              color={isGem ? "pink" : "green"}
              className="w-full py-2 font-black font-saira tracking-[0.005em] flex justify-center items-center gap-2 shrink-0"
            >
              {isPlayAgainLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  FINDING MATCH
                </>
              ) : (
                <>
                  PLAY AGAIN {hideAmounts ? DEMO_AMOUNT_LABEL.entry : <><CurrencyIcon code={displayCurrency} className="h-4 w-4" />{entryFeeValue}</>} ENTRY
                </>
              )}
            </CustomButton>
          </div>
        )}

        {matchId && (
          <div className="px-6 pb-4 text-center text-[10px] text-darkGray break-all select-all">
            Match ID: {matchId}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchResultSummaryPopUp;
